// Datos medidos del modelo para los gráficos (guía 11). Se copian acá porque
// referencia/ queda fuera de la app y no se importa en el build. Si cambian los
// archivos de origen, hay que volver a copiar estos números.
//
// - perplexity: referencia/datos/depth_sweep_reasoning.json → curves.forced y
//   curves.halting (ppl redondeada a 4 decimales; avg_actual_iters sin redondear).
// - bpc: referencia/datos/comparacion_gpt2.json (bpc a 4 decimales; chars tal cual).
// - charsPerToken y deliberationShare: referencia/datos/model-card-nano.md.

export type PerplexityPoint = {
  /** Vueltas de deliberación (k). */
  depth: number;
  /** Perplejidad con k vueltas fijas (curves.forced). */
  forced: number;
  /** Perplejidad con parada adaptativa y tope k (curves.halting). */
  halting: number;
  /** Vueltas que usó en promedio la parada adaptativa (curves.halting.avg_actual_iters). */
  haltingAvgIters: number;
};

export const perplexitySweep: PerplexityPoint[] = [
  { depth: 1, forced: 69.86, halting: 69.86, haltingAvgIters: 1 },
  { depth: 2, forced: 54.9413, halting: 54.9634, haltingAvgIters: 2 },
  { depth: 3, forced: 51.3097, halting: 51.9705, haltingAvgIters: 3 },
  { depth: 4, forced: 50.0093, halting: 50.813, haltingAvgIters: 4 },
  { depth: 5, forced: 49.4646, halting: 50.3481, haltingAvgIters: 5 },
  { depth: 6, forced: 49.3011, halting: 50.2302, haltingAvgIters: 6 },
  { depth: 7, forced: 49.2767, halting: 50.2535, haltingAvgIters: 7 },
  { depth: 8, forced: 49.2811, halting: 50.2534, haltingAvgIters: 7.375 },
  { depth: 9, forced: 49.2812, halting: 50.2534, haltingAvgIters: 7.385416666666667 },
  { depth: 10, forced: 49.2812, halting: 50.2534, haltingAvgIters: 7.395833333333333 },
  { depth: 11, forced: 49.2812, halting: 50.2534, haltingAvgIters: 7.40625 },
  { depth: 12, forced: 49.2812, halting: 50.2534, haltingAvgIters: 7.40625 },
  { depth: 13, forced: 49.2812, halting: 50.2534, haltingAvgIters: 7.40625 },
  { depth: 14, forced: 49.2812, halting: 50.2534, haltingAvgIters: 7.40625 },
  { depth: 15, forced: 49.2812, halting: 50.2534, haltingAvgIters: 7.40625 },
  { depth: 16, forced: 49.2812, halting: 50.2534, haltingAvgIters: 7.40625 },
];

/** Vuelta con la menor perplejidad forzada (el "verdict" del JSON: mejor k = 7). */
export const optimalDepth = 7;

/** Profundidad con la que se entrenó (trained_max_iters del JSON). */
export const trainedMaxIters = 8;

/** Contexto del barrido (model card, sección "La profundidad recurrente SI aporta"). */
export const perplexitySource = {
  dataset: "holdout GSM8K-es",
  rows: 192,
  file: "depth_sweep_reasoning.json",
};

export type BpcRow = {
  id: "es" | "en";
  language: string;
  /** Qué texto se usó (model card, tabla "Contra GPT-2 124M"). */
  corpus: string;
  /** Caracteres del texto de evaluación, el mismo para los dos modelos. */
  chars: number;
  yvyrai: number;
  gpt2: number;
};

export const bpcComparison: BpcRow[] = [
  { id: "es", language: "Español", corpus: "web, held-out", chars: 150341, yvyrai: 1.3625, gpt2: 2.2238 },
  { id: "en", language: "Inglés", corpus: "Wikipedia", chars: 444665, yvyrai: 2.0078, gpt2: 1.028 },
];

export const bpcSource = { file: "comparacion_gpt2.json" };

/** Caracteres por token en español (model card): el tokenizer propio comprime 46 % mejor. */
export const charsPerToken = { yvyrai: 4.25, gpt2: 2.92, gainPercent: 46 };

/** Con 8 vueltas, la deliberación es ~10 % de los parámetros y ~40 % del cómputo (model card). */
export const deliberationShare = { paramsPercent: 10, computePercent: 40, atDepth: 8 };

/**
 * Número con coma decimal (guía 6). Se formatea a mano y no con Intl para que el
 * servidor y el navegador den exactamente el mismo texto.
 */
export function decimal(n: number, digits: number): string {
  return n.toFixed(digits).replace(".", ",");
}

/** Separador de miles con punto (25.700, 1.024). */
export function thousands(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}
